import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Images as Albums, Plus, Trash2, ExternalLink, Lock, Pencil, X, Check } from "lucide-react";

/**
 * Super Albums panel — list, create, edit and delete grouped event albums.
 * Rendered on the host dashboard. Any paid host can create one.
 */
const SuperAlbumsPanel = () => {
  const [albums, setAlbums] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | album
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [a, e] = await Promise.all([
        api.get("/super-albums"),
        api.get("/events"),
      ]);
      setAlbums(a.data);
      setEvents(e.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to load Super Albums.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this Super Album? The underlying events are untouched.")) return;
    await api.delete(`/super-albums/${id}`);
    setAlbums(a => a.filter(x => x.id !== id));
  };

  const copyLink = async (slug) => {
    const url = `${window.location.origin}/album/${slug}`;
    await navigator.clipboard.writeText(url);
  };

  return (
    <section className="bg-white rounded-3xl soft-shadow p-6 md:p-8" data-testid="super-albums-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 text-coral-500">
            <Albums className="w-5 h-5" />
            <h2 className="font-serif italic text-2xl md:text-3xl text-navy-700">Super Albums</h2>
          </div>
          <p className="text-navy-700/60 text-sm mt-1">
            Group related events into one private showcase guests can browse together.
          </p>
        </div>
        <button
          onClick={() => setEditing({})}
          data-testid="super-album-new-btn"
          className="inline-flex items-center gap-2 bg-coral-300 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-coral-400"
        >
          <Plus className="w-4 h-4" /> New album
        </button>
      </div>

      {err && <div className="mb-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</div>}

      {loading ? (
        <div className="text-navy-700/50 text-sm py-6">Loading albums…</div>
      ) : albums.length === 0 ? (
        <div className="text-navy-700/60 text-sm bg-cream-100 rounded-xl p-6 text-center">
          No Super Albums yet. Group a wedding, rehearsal and brunch into one shareable showcase.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {albums.map(a => (
            <div
              key={a.id}
              data-testid={`super-album-card-${a.id}`}
              className="border border-cream-200 rounded-2xl p-4 bg-cream-100/40 flex flex-col"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif italic text-navy-700 text-xl truncate">{a.title}</h3>
                  <div className="text-xs text-navy-700/60 mt-1">
                    {a.events?.length || 0} events · {a.has_passcode ? "Passcode protected" : "Guest-list access"}
                  </div>
                </div>
                {a.has_passcode && <Lock className="w-4 h-4 text-navy-700/40 shrink-0" />}
              </div>
              {a.events?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {a.events.slice(0, 5).map(ev => (
                    <span key={ev.id} className="text-[11px] font-medium bg-white border border-cream-200 px-2 py-0.5 rounded-full text-navy-700/70 truncate max-w-[160px]">
                      {ev.name}
                    </span>
                  ))}
                  {a.events.length > 5 && <span className="text-[11px] text-navy-700/50">+{a.events.length - 5}</span>}
                </div>
              )}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setEditing(a)}
                  data-testid={`super-album-edit-${a.id}`}
                  className="inline-flex items-center gap-1.5 bg-navy-700 text-white px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-navy-800"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <a
                  href={`/album/${a.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`super-album-open-${a.id}`}
                  className="inline-flex items-center gap-1.5 border border-navy-700/20 text-navy-700 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-white"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open
                </a>
                <button
                  onClick={() => copyLink(a.slug)}
                  data-testid={`super-album-copy-${a.id}`}
                  className="inline-flex items-center gap-1.5 border border-navy-700/20 text-navy-700 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-white"
                >
                  Copy link
                </button>
                <button
                  onClick={() => remove(a.id)}
                  data-testid={`super-album-delete-${a.id}`}
                  className="ml-auto inline-flex items-center gap-1.5 border border-red-200 text-red-500 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-red-50"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <SuperAlbumEditor
          album={editing}
          events={events}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
};


const SuperAlbumEditor = ({ album, events, onClose, onSaved }) => {
  const isNew = !album?.id;
  const [title, setTitle] = useState(album?.title || "");
  const [description, setDescription] = useState(album?.description || "");
  const [coverImage, setCoverImage] = useState(album?.cover_image || "");
  const [passcode, setPasscode] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set(album?.events?.map(e => e.id) || album?.event_ids || []));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toggle = (id) => {
    setErr("");
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setErr("");
    if (!title.trim()) { setErr("Give the album a title."); return; }
    if (selectedIds.size === 0) { setErr("Pick at least one event."); return; }
    setBusy(true);
    try {
      const body = {
        title: title.trim(),
        event_ids: Array.from(selectedIds),
        cover_image: coverImage.trim() || null,
        description: description.trim() || null,
      };
      if (passcode) body.passcode = passcode.trim();
      if (isNew) {
        await api.post("/super-albums", body);
      } else {
        // On edit, only send passcode if the host typed something new.
        // Empty string resets the passcode (host confirmed in UI).
        await api.patch(`/super-albums/${album.id}`, body);
      }
      onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not save the album.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid="super-album-editor"
    >
      <div
        className="bg-white w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-cream-200">
          <h3 className="font-serif italic text-navy-700 text-2xl">
            {isNew ? "New Super Album" : "Edit Super Album"}
          </h3>
          <button
            onClick={onClose}
            data-testid="super-album-editor-close"
            className="w-9 h-9 rounded-full bg-cream-100 hover:bg-cream-200 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Title</label>
            <input
              value={title}
              onChange={e => { setErr(""); setTitle(e.target.value); }}
              placeholder="The Big Weekend"
              maxLength={120}
              data-testid="super-album-title-input"
              className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Short description (optional)</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Everything from the rehearsal dinner to the Sunday brunch."
              rows={2}
              maxLength={2000}
              className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Cover image URL (optional)</label>
            <input
              value={coverImage}
              onChange={e => setCoverImage(e.target.value)}
              placeholder="https://…"
              className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Events in this album</label>
            <p className="text-xs text-navy-700/60 mt-1">Pick 1–20 events. Guests will browse them in the order you select.</p>
            <div className="mt-3 border border-cream-200 rounded-xl divide-y divide-cream-200 max-h-56 overflow-y-auto">
              {events.length === 0 ? (
                <div className="px-4 py-6 text-sm text-navy-700/50 text-center">No events yet.</div>
              ) : events.map(e => (
                <label
                  key={e.id}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-cream-100 cursor-pointer"
                  data-testid={`super-album-event-pick-${e.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(e.id)}
                    onChange={() => toggle(e.id)}
                    className="w-4 h-4 accent-coral-400"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-navy-700 truncate">{e.name}</div>
                    <div className="text-xs text-navy-700/50">{e.type} · {e.upload_count} uploads{e.date ? ` · ${e.date}` : ""}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">
              Passcode {album?.has_passcode && "(set)"} (optional)
            </label>
            <input
              value={passcode}
              onChange={e => setPasscode(e.target.value)}
              placeholder={album?.has_passcode ? "Type a new passcode to replace, or leave blank" : "e.g. 1234 — min 4 chars"}
              maxLength={12}
              data-testid="super-album-passcode-input"
              className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300"
            />
            <p className="text-xs text-navy-700/60 mt-1.5">
              Guests unlock with this passcode <strong>or</strong> an email that's on any included event's guest list.
            </p>
          </div>
          {err && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</div>}
        </div>
        <div className="px-6 py-4 border-t border-cream-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-navy-700 hover:bg-cream-100 rounded-full"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            data-testid="super-album-save-btn"
            className="inline-flex items-center gap-2 bg-coral-300 text-white px-5 py-2 rounded-full font-semibold hover:bg-coral-400 disabled:opacity-60"
          >
            <Check className="w-4 h-4" /> {busy ? "Saving…" : (isNew ? "Create album" : "Save changes")}
          </button>
        </div>
      </div>
    </div>
  );
};


export default SuperAlbumsPanel;
